import Video, { LocalVideoTrack, Logger } from "twilio-video";
import reduxStore from "../../configureEnvStore";
import { NETWORK_QUALITY_LEVEL, ROOM_TYPE } from "../../constants/Constants";
import { CALL_DATA } from "../../constants/PublicVariables";
import { LogFirebase } from "../../services/firestore";
import { dualStreamEnabled } from "../../shared/utility";
import { getLocalAudioTrack, getLocalTracks, getLocalVideoTrack, getRoom, resetRoom, resetRoomState, setLocalAudioTrack } from "./Room/roomService";
import Localize from "../../locale/Localize";
import { DefaultLanguage } from "../../constants/LanguageConstants";
import { setIsAudioTrackAttchedForAnyParticipant, setIsLocalVideoAttached } from "../../reducers/twilioEvents-reducer";
import { getAudioParams, handleEnableDisableAudio, remoteParticipantAudioChecker } from "./AudioCall/AudioCallUtils";
import mediaDevice from "../../services/mediaDeviceService";
// import { VideoRoomMonitor } from "@twilio/video-room-monitor";
import { setStartCallSettingLocalVideo } from "../../reducers/call-setting-reducer";
import { setIsVideoButtonClickDisable } from "../../reducers/common-reducer";

// create local room
export const createMyRoom = (token, videoParams) => {
    return new Promise((resolve, reject) => {
        Video.connect(token, videoParams)
            .then((room) => {
                resolve(room);
            })
            .catch((error) => {
                reject(error);
                console.log("Error joining the call", error);
            });
    });
};

// create local video track
export const createMyLocalVideoTrack = (videoParams) => {
    return new Promise((resolve, reject) => {
        Video.createLocalVideoTrack(videoParams).then(
            function (localVideoTrack) {
                resolve(localVideoTrack);
            },
            function (error) {
                reject(error);
            }
        );
    });
};

// create local audio track
export const createMyLocalAudioTrack = (audioParam) => {
    return new Promise((resolve, reject) => {
        Video.createLocalAudioTrack(audioParam).then(
            function (audioTrack) {
                console.log(audioTrack);
                resolve(audioTrack);
            },
            function (error) {
                console.log("local track creation failed");
                console.error(error);
                reject(error);
            }
        );
    });
};

// create local screen share track
export const createMyLocalScreenShareTrack = (mediaStream, params) => {
    return new Video.LocalVideoTrack(mediaStream.getVideoTracks()[0], params);
};

// create local data track
export const createMyLocalDataTrack = () => {
    return new Video.LocalDataTrack({ name: setTrackName("data") });
};

export const setTrackName = (kind) => {
    let name = "";
    if (kind == "video") {
        name = "v_" + CALL_DATA.memberId;
    } else if (kind == "audio") {
        name = "a_" + CALL_DATA.memberId;
    } else if (kind == "data") {
        name = "d_" + CALL_DATA.memberId;
    } else if (kind == "screenShare") {
        name = "s_" + CALL_DATA.memberId;
    }
    return name;
};

export const showMeRoomTypeIsGoRoomOrP2P = (FEATUREFLAGS) => {
    if (!FEATUREFLAGS) {
        return false;
    }
    return FEATUREFLAGS?.SHOWMEROOMTYPE === ROOM_TYPE.P2P || FEATUREFLAGS?.SHOWMEROOMTYPE === ROOM_TYPE.GO;
};

export const showMeRoomTypeIsGORoom = (FEATUREFLAGS) => {
    if (!FEATUREFLAGS) {
        return false;
    }
    return FEATUREFLAGS?.SHOWMEROOMTYPE === ROOM_TYPE.GO;
};

export const getActiveRoom = () => {
    const room = getRoom();
    return room;
};

export const getCallConnectParams = (joinMeetingData) => {
    let preferredVideoCodec = "H264";

    // Determine the preferred video codec based on the feature flag (if vuzix/epson support is on in SA)
    if (joinMeetingData?.sessionConfigurations.VIDEOCODEC === "VP8") {
        preferredVideoCodec = "VP8";
    }

    //logger used from SDK 2.10.0
    const logger = Logger.getLogger("twilio-video");
    if (process.env.NODE_ENV === "production") {
        logger.setLevel("silent");
    } else {
        logger.setLevel("debug");
    }

    // Basic video parameters
    const videoParams = { name: CALL_DATA.roomName };
    const { videoQualityResolutionParams } = reduxStore.getState().callSetting; //set property backend option wise
    console.log("videoQualityResolutionParams: ", videoQualityResolutionParams);
    if (!showMeRoomTypeIsGoRoomOrP2P(joinMeetingData?.sessionConfigurations)) {
        // The Network Quality API is only available Group Rooms (including Small Group Rooms).
        videoParams.networkQuality = {
            local: 3, // LocalParticipant's Network Quality verbosity
            remote: 3, // RemoteParticipants' Network Quality verbosity
        };

        // The Network Bandwidth Profile API is only available in Group Rooms (including Small Group Rooms).
        videoParams.bandwidthProfile = {
            video: {
                contentPreferencesMode: "auto", //used instead of rednerDimension after Twilio 2.15
            },
        };

        // Set maximum subscription bitrate if available
        if (videoQualityResolutionParams?.MAXSUBBITRATE) {
            videoParams.bandwidthProfile.video.maxSubscriptionBitrate = videoQualityResolutionParams.MAXSUBBITRATE;
        }

        // Set video mode if specified
        if (videoQualityResolutionParams?.MODE) {
            videoParams.bandwidthProfile.video.mode = videoQualityResolutionParams.MODE;
        }
    }

    // Set audio bitrate and simulcast options for VP8 codec
    if (videoQualityResolutionParams?.MAXAUDIOBITRATE) {
        videoParams.maxAudioBitrate = videoQualityResolutionParams.MAXAUDIOBITRATE;
    }
    if (preferredVideoCodec == "VP8" && videoQualityResolutionParams?.SIMULCAST) {
        preferredVideoCodec = { codec: "VP8", simulcast: true };
    }

    videoParams.preferredAudioCodecs = ["opus", "isac"];
    videoParams.preferredVideoCodecs = [preferredVideoCodec];
    videoParams.receiveTranscriptions = true; // Enable live transcription

    // Enable track warning notifications for telemetry logging
    // Required for trackWarning events to fire (see: twilio-video.js v2.22.0 CHANGELOG)
    videoParams.notifyWarnings = ["recording-media-lost"];

    console.log("videoParams: ", videoParams);
    return videoParams;
};

export const getPublishVideoContainer = (fromWhere) => {
    let container = "";
    if (dualStreamEnabled()) {
        if (fromWhere == "screenshare") {
            container = document.getElementById("expandedVideo");
        } else {
            container = document.getElementById("galleryVideo_" + CALL_DATA.localIdentity);
        }
    } else {
        container = document.getElementById("expandedVideo");
    }
    return container;
};

export const getVideoContainer = (identity) => {
    let container = null;
    if (dualStreamEnabled()) {
        container = document.getElementById("galleryVideo_" + identity);
    } else {
        container = document.getElementById("expandedVideo");
    }
    return container;
};

/* Get the specific slick participant element from the DOM. */
export const getSlickParticipantVideoContainer = (identity) => document.getElementById("slickVideo_" + identity);

export const getTracksByType = (participant, trackType) => {
    const alltracks = getTracksFromPublication(participant);
    const typeTracks = [];
    alltracks.forEach(function (track) {
        if (track.kind == trackType) {
            typeTracks.push(track);
        }
    });
    return typeTracks;
};

export const getTracksFromPublication = (participant) => {
    return Array.from(participant?.tracks?.values())
        .filter(function (publication) {
            return publication.track;
        })
        .map(function (publication) {
            return publication.track;
        });
};

export const attachTracks = function (tracks, container, from, callback) {
    if (container) {
        tracks.forEach((track) => {
            if (track.kind === "data") return;
            const element = track.attach();
            if (track.kind === "video") {
                const { startLocalVideo } = reduxStore.getState().twilioEvents;
                const { isCallSettingDrawerOpen, isVideoButtonClickDisable } = reduxStore.getState().commonReducer;

                if (track instanceof LocalVideoTrack) {
                    if (startLocalVideo) {
                        reduxStore.dispatch(setIsLocalVideoAttached(true)); // This is a local video track
                    }
                    if (isCallSettingDrawerOpen && !startLocalVideo) {
                        reduxStore.dispatch(setStartCallSettingLocalVideo(true));
                    }
                }
                // Mute video track and attach it to the container
                element.muted = true;
                container.appendChild(element);
                LogFirebase("TwilioEvents", "videoTrackAttached", container?.id);
                const videoElement = container.querySelector("video");
                if (dualStreamEnabled() && container?.id?.includes("galleryVideo_")) {
                    videoElement?.classList.add("gallery-view-video");
                } else if (isCallSettingDrawerOpen) {
                    videoElement?.classList.add("video");
                } else {
                    videoElement?.classList.add("session-video");
                }
                if (dualStreamEnabled() && container?.id?.includes("slickVideo_")) {
                    // attach Local and Remote Video/Screen Share tracks in the specific slick participant container
                    videoElement?.classList.add("participant-video"); // added participant-video class in Video element
                }
                if (from === "fromCallBack" && callback) {
                    callback(element);
                }
                if (isVideoButtonClickDisable) {
                    reduxStore.dispatch(setIsVideoButtonClickDisable(false));
                }
            } else if (track.kind === "audio") {
                // Attach audio track directly
                const isAudioTrackAttchedForAnyParticipant = reduxStore.getState().twilioEvents?.isAudioTrackAttchedForAnyParticipant;
                container.appendChild(element);
                LogFirebase("TwilioEvents", track.kind + "TrackAttached", container?.id);
                if (!isAudioTrackAttchedForAnyParticipant) {
                    reduxStore.dispatch(setIsAudioTrackAttchedForAnyParticipant(true));
                    remoteParticipantAudioChecker.start();
                }
            }
        });
    } else {
        LogFirebase("TwilioEvents", "container", "doesn't exist in :attachTracks()");
        console.error("container doesn't exist in :attachTracks()");
    }
};

export const detachTracks = (tracks) => {
    tracks.forEach(function (track) {
        if (track.kind != "data") {
            if (track instanceof LocalVideoTrack) {
                reduxStore.dispatch(setIsLocalVideoAttached(false)); // Detached a LOCAL track
            }
            track.detach().forEach(function (detachedElement) {
                detachedElement.remove();
            });
        }
    });
};

export const removeVideoElement = (previewContainer) => {
    if (previewContainer) {
        const videos = previewContainer.querySelectorAll("video");

        if (videos?.length > 0) {
            LogFirebase("TwilioEvents", "RemovingVideoElements", { videoLength: videos.length, containerId: previewContainer?.id });
            videos.forEach((video) => previewContainer.removeChild(video));
        } else {
            LogFirebase("TwilioEvents", "NoVideoElementsFound", "in removeVideoElement()");
            console.log("No video elements found in the container");
        }
    }
};

export const removeLocalTracks = (localTracks) => {
    if (localTracks) {
        localTracks.forEach(function (track) {
            track.stop();
            track.detach().forEach(function (detachedElement) {
                detachedElement.remove();
            });
        });
    }
    localTracks = null;
};

export const detachParticipantTracks = function (participant) {
    const tracks = getTracksFromPublication(participant);
    console.log("::detachParticipantTracks=" + participant.identity);
    console.log(tracks);
    if (tracks != null) {
        detachTracks(tracks);
    }
};

export const discontinueRoom = () => {
    const localTracks = getLocalTracks();
    const room = getRoom();

    if (localTracks) {
        removeLocalTracks(localTracks);
    }

    detachParticipantTracks(room.localParticipant);
    room?.participants?.forEach(detachParticipantTracks);
    room?.disconnect();
    resetRoom(null);
    resetRoomState(null);
};

export const restartLocalVideoTracks = (params) => {
    const localVideoTrack = getLocalVideoTrack();
    if (localVideoTrack) {
        localVideoTrack.stop(); //require for ipad os 13
        return localVideoTrack.restart(params);
    }
};

/**disables first audio tracks and then enables it again */
const disableEnableAudiotracks = () => {
    const room = getRoom();
    if (reduxStore.getState().twilioEvents?.startLocalAudio) {
        const localAudioTracks = getTracksByType(room.localParticipant, "audio");
        localAudioTracks.forEach((audioTrack) => {
            audioTrack.disable();
            setTimeout(() => {
                audioTrack.enable();
            }, 500);
        });
    }
};

/**function used on ios app  minimizes-it will disable and enable audio tracks */
export const createEnableAudiotracks = (audiotracksArr) => {
    if (reduxStore.getState().twilioEvents?.startLocalAudio) {
        //mic is ON
        if (audiotracksArr?.length > 0) {
            disableEnableAudiotracks(); //track exist so just disable and enable the audio tracks
        } else {
            createMyLocalAudioTrack(); //tracks doesn't exist so create new
        }
    }
};

export const removeLocalAudioElement = (fromWhere) => {
    const room = getRoom();

    if (room) {
        //audio tracks will be detached from discontinue() fun on end call. unpublush and remove from js variables

        const localAudioTracks = getTracksByType(room.localParticipant, "audio");
        LogFirebase("Audio", "going_to_remove_Local_AudioElement", { localAudioTrackLength: localAudioTracks.length, room_Name: room.name });
        room.localParticipant.unpublishTracks(localAudioTracks);

        const localAudioTrack = getLocalAudioTrack();
        if (localAudioTrack) {
            localAudioTrack.disable();
            localAudioTrack.stop();
            localAudioTrack.detach().forEach(function (detachedElement) {
                detachedElement.remove();
            });
            setLocalAudioTrack(null);
        }
        LogFirebase("Audio", "removed_Local_AudioElement", { localAudioTrackLength: localAudioTracks.length, room_Name: room.name });

        if (fromWhere && fromWhere == "endCall") {
            //remove audio tag incase if not removed
            const previewContainer = document.getElementById("audioTag");
            if (previewContainer.querySelector("audio")) {
                previewContainer.removeChild(previewContainer.querySelector("audio"));
            }
        }
    }
};

export const videoTracksExist = () => {
    const room = getRoom();
    let videoTracksExist = false;
    let trackArr = null;
    room?.participants?.forEach(function (remoteParticipant, val) {
        trackArr = getTracksByType(remoteParticipant, "video");
        if (trackArr && trackArr.length > 0) {
            videoTracksExist = true;
        }
    });
    return videoTracksExist;
};

export const getNetworkQuality = (networkQualityLevel, localisedValues = Localize[DefaultLanguage]) => {
    let networkQuality = { percentage: "", name: "" };
    switch (networkQualityLevel) {
        case 0:
            networkQuality.name = localisedValues["reconnecting"] + "...";
            break;
        case NETWORK_QUALITY_LEVEL.VERY_BAD_VALUE:
            networkQuality.name = localisedValues["very_bad"];
            networkQuality.percentage = "20%";
            break;
        case NETWORK_QUALITY_LEVEL.BAD_VALUE:
            networkQuality.name = localisedValues["bad"];
            networkQuality.percentage = "40%";
            break;
        case NETWORK_QUALITY_LEVEL.POOR_VALUE:
            networkQuality.name = localisedValues["poor"];
            networkQuality.percentage = "60%";
            break;
        case NETWORK_QUALITY_LEVEL.GOOD_VALUE:
            networkQuality.name = localisedValues["good"];
            networkQuality.percentage = "80%";
            break;
        case NETWORK_QUALITY_LEVEL.EXCELLENT_VALUE:
            networkQuality.name = localisedValues["excellent"];
            networkQuality.percentage = "100%";
            break;
    }
    return networkQuality;
};

/**restarts audio tracks requires for android 8,9 on change audio input inside call settings.. */
export const restartLocalAudioTracks = function (params) {
    const localAudioTrack = getLocalAudioTrack();
    if (localAudioTrack) {
        //restart for audio tracks only..
        localAudioTrack.stop();
        return localAudioTrack.restart(params);
    }
};

/**this will activate mic testing on local audio tracks */
export const activateAudioTrackMicTesting = function () {
    let objStream = new MediaStream();
    const localAudioTrack = getLocalAudioTrack();
    objStream.addTrack(localAudioTrack.mediaStreamTrack);
    mediaDevice.startTestingMic(objStream);
};

/**this will apply audio change in Twilio call */
export const applyAudioInputDeviceSelection = () => {
    const localAudioTrack = getLocalAudioTrack();
    if (localAudioTrack) {
        const audioParam = getAudioParams();
        restartLocalAudioTracks(audioParam).then(function () {
            mediaDevice.hideCallSettingCamLoading();
            activateAudioTrackMicTesting();
            const room = getActiveRoom();
            const localAudioTracks = getTracksByType(room?.localParticipant, "audio");
            localAudioTracks.forEach(function (audioTrack) {
                handleEnableDisableAudio(false, audioTrack, false); // going to disable audio
                handleEnableDisableAudio(true, audioTrack, true); // going to disable audio
            });
        });
    }
};

export const registerVideoRoomMonitor = (room) => {
    // if (GLOBAL_MEETING_DATA.isAgent && (GLOBAL_MEETING_DATA.isRoomCallHasLogsParams == ENABLED || PublicVariables.ldFlags?.roomCallLogs)) {
    //     VideoRoomMonitor.registerVideoRoom(room || getActiveRoom());
    //     VideoRoomMonitor.on("opened", () => console.log("the monitor has been opened"));
    //     VideoRoomMonitor.on("closed", () => console.log("the monitor has been closed"));
    // }
};
